/**
 * ZIP indirme utility fonksiyonları
 * Birden fazla dosyayı toplu olarak ZIP formatında indirmek için kullanılır
 */

/**
 * JSZip kütüphanesini dinamik olarak yükler
 * @returns {Promise<Object>} JSZip constructor
 */
export const loadJSZip = () => {
    return new Promise((resolve, reject) => {
        // Zaten yüklü mü kontrol et
        if (window.JSZip) {
            resolve(window.JSZip);
            return;
        }

        const scriptId = "jszip-cdn-script";
        const existing = document.getElementById(scriptId);

        if (existing) {
            // Script zaten eklendi ama henüz yüklenmedi, yüklenmesini bekle
            const checkInterval = setInterval(() => {
                if (window.JSZip) {
                    clearInterval(checkInterval);
                    resolve(window.JSZip);
                }
            }, 100);

            // 10 saniye timeout
            setTimeout(() => {
                clearInterval(checkInterval);
                if (!window.JSZip) {
                    reject(new Error("JSZip yüklenemedi (timeout)"));
                }
            }, 10000);
            return;
        }

        // Script'i ekle
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
        script.async = true;
        script.onload = () => {
            if (window.JSZip) {
                resolve(window.JSZip);
            } else {
                reject(new Error("JSZip yüklendi ama window.JSZip bulunamadı"));
            }
        };
        script.onerror = () => reject(new Error("JSZip script yüklenemedi"));
        document.head.appendChild(script);
    });
};

/**
 * Belirtilen URL'leri indirip ZIP dosyası olarak toplar
 * @param {Array<string>} urls - İndirilecek dosya URL'leri
 * @param {string} zipFileName - ZIP dosyasının adı (uzantısız)
 * @param {Object} options - Opsiyonel ayarlar
 * @param {Function} options.onProgress - Progress callback (currentIndex, totalCount, fileName)
 * @param {Function} options.onSuccess - Başarı callback (successCount, failCount)
 * @param {Function} options.onError - Hata callback (error)
 * @returns {Promise<Object>} İndirme sonuç bilgisi { successCount, failCount, failedUrls }
 */
export const downloadUrlsAsZip = async (urls, zipFileName = "files", options = {}) => {
    const { onProgress, onSuccess, onError } = options;

    if (!Array.isArray(urls) || urls.length === 0) {
        const error = new Error("İndirilecek dosya bulunamadı");
        if (onError) onError(error);
        throw error;
    }

    let successCount = 0;
    let failCount = 0;
    const failedUrls = [];

    try {
        // JSZip'i yükle
        const JSZip = await loadJSZip();
        const zip = new JSZip();

        // Her URL için sırayla işlem yap
        for (let i = 0; i < urls.length; i++) {
            const url = urls[i];

            // Progress callback
            if (onProgress) {
                onProgress(i + 1, urls.length, url);
            }

            try {
                console.log(`[${i + 1}/${urls.length}] İndiriliyor:`, url);

                // Fetch ile dosyayı indir
                const response = await fetch(url, {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    credentials: "omit",
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                // Blob'a dönüştür
                const blob = await response.blob();

                // Dosya adını oluştur
                const urlParts = url.split("/");
                const originalName = urlParts[urlParts.length - 1] || `file_${i + 1}`;
                const fileName = `${String(i + 1).padStart(3, "0")}_${originalName}`;

                // ZIP'e ekle
                zip.file(fileName, blob);
                successCount++;
                console.log(`✓ Başarılı [${i + 1}/${urls.length}]:`, fileName);
            } catch (error) {
                failCount++;
                failedUrls.push(url);
                console.error(`✗ Hata [${i + 1}/${urls.length}]:`, url, error.message);
            }
        }

        // Hiç dosya indirilemediyse
        if (successCount === 0) {
            const error = new Error("Hiçbir dosya indirilemedi! Lütfen internet bağlantınızı kontrol edin.");
            console.error("Başarısız URL'ler:", failedUrls);
            if (onError) onError(error);
            throw error;
        }

        // ZIP dosyasını oluştur
        console.log("ZIP oluşturuluyor...");

        const content = await zip.generateAsync({
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: { level: 6 },
        });

        // ZIP'i indir
        const a = document.createElement("a");
        const blobUrl = URL.createObjectURL(content);
        a.href = blobUrl;
        a.download = `${zipFileName}_${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();

        // Temizlik
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        }, 100);

        const result = { successCount, failCount, failedUrls };

        // Başarı callback
        if (onSuccess) {
            onSuccess(successCount, failCount);
        }

        console.log(`✓ İndirme tamamlandı: ${successCount} başarılı, ${failCount} başarısız`);
        return result;
    } catch (error) {
        console.error("ZIP oluşturma hatası:", error);
        if (onError) onError(error);
        throw error;
    }
};
